import { useQuery } from '@tanstack/react-query';
import { httpClient } from '@shared/api';

/** V2-4 этап 4: база знаний по экранам 5–5в (бэкенд `knowledge_batch.py`). */

export type KbState = 'ready' | 'processing' | 'queued' | 'awaiting' | 'error';

export interface KbSource {
  id: string;
  type: 'file' | 'website' | 'catalog';
  name: string | null;
  display_name: string | null;
  status: string;
  state: KbState;
  created_at: string;
  est_pages: number | null;
  est_images: number | null;
  process_images: boolean;
  confirm_expires_at: string | null;
  autostart: boolean;
  estimate_rub: string | null;
  charged_rub: string;
  reserved_rub: string;
  percent: number | null;
  step: number | null;
  step_label: string | null;
  images_total: number | null;
  images_done: number | null;
  pages_total: number | null;
  pages_done: number | null;
  site_pages: number | null;
  started_at: number | null;
  error: string | null;
}

export interface KbOverview {
  sources: KbSource[];
  show_images: boolean;
  finished_since_count: number;
  finished_since_charged_rub: string;
}

export interface Funds {
  balance_rub: string;
  bonus_left_rub: string;
  /** Обещано запущенным файлам, резерв которых ещё не сделан. */
  committed_rub?: string;
  /** Можно потратить на новую загрузку: баланс − обещанное (Р38). */
  available_rub?: string;
}

export interface UploadItem {
  source_id: string;
  file_name: string;
  file_size_bytes: number;
  page_count: number;
  images_count: number;
  estimated_cost_with_vision_rub: string | number;
  estimated_cost_without_vision_rub: string | number;
}

export interface ZipUpload {
  archive_name: string;
  items: UploadItem[];
  skipped: Array<{ file_name: string; reason: string }>;
}

export interface KbSuggestion {
  signature: string | null;
  topics: string | null;
  items: string[];
  dismissed: boolean;
  pending: boolean;
}

export const kbKeys = {
  overview: (aid: string) => ['kb', 'overview', aid] as const,
  funds: ['billing', 'funds'] as const,
  suggestion: (aid: string) => ['kb', 'suggestion', aid] as const,
};

export const kbApi = {
  async overview(aid: string, since?: string): Promise<KbOverview> {
    return (
      await httpClient.get<KbOverview>('/knowledge/overview', {
        params: { assistant_id: aid, since },
      })
    ).data;
  },
  async funds(): Promise<Funds> {
    return (await httpClient.get<Funds>('/billing/funds')).data;
  },
  async uploadFile(aid: string, file: File): Promise<UploadItem> {
    const form = new FormData();
    form.append('assistant_id', aid);
    form.append('file', file);
    const r = await httpClient.post<UploadItem>('/knowledge/upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600_000,
    });
    return r.data;
  },
  async uploadZip(aid: string, file: File): Promise<ZipUpload> {
    const form = new FormData();
    form.append('assistant_id', aid);
    form.append('file', file);
    const r = await httpClient.post<ZipUpload>('/knowledge/upload-zip', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 600_000,
    });
    return r.data;
  },
  async confirmBatch(aid: string, ids: string[], processImages: boolean) {
    return (
      await httpClient.post('/knowledge/sources/confirm-batch', {
        assistant_id: aid,
        source_ids: ids,
        process_images: processImages,
      })
    ).data;
  },
  /** Запустить то, что ждало оплаты (если денег уже хватает). */
  async startWaiting(aid: string): Promise<{ started: number }> {
    return (await httpClient.post('/knowledge/sources/start-waiting', { assistant_id: aid })).data;
  },
  async removePending(id: string) {
    await httpClient.delete(`/knowledge/sources/${id}/pending`);
  },
  async suggestion(aid: string): Promise<KbSuggestion> {
    return (
      await httpClient.get<KbSuggestion>(`/assistants/${aid}/kb-suggestion`, { timeout: 90_000 })
    ).data;
  },
  async dismissSuggestion(aid: string, signature: string) {
    await httpClient.post(`/assistants/${aid}/kb-suggestion/dismiss`, { signature });
  },
};

/** Обзор базы знаний; пока что-то обрабатывается — обновляем каждые 3 с. */
export function useKbOverview(aid: string | undefined) {
  return useQuery<KbOverview, Error>({
    queryKey: kbKeys.overview(aid ?? ''),
    queryFn: () => kbApi.overview(aid!),
    enabled: !!aid,
    refetchInterval: (q) =>
      q.state.data?.sources.some((s) => s.state === 'processing' || s.state === 'queued')
        ? 3000
        : false,
  });
}

export function useFunds() {
  return useQuery<Funds, Error>({
    queryKey: kbKeys.funds,
    queryFn: kbApi.funds,
    // Перед запуском деньги должны быть свежими (Р38): только что запущенные
    // файлы уменьшают доступную сумму.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useKbSuggestion(aid: string | undefined) {
  return useQuery<KbSuggestion, Error>({
    queryKey: kbKeys.suggestion(aid ?? ''),
    queryFn: () => kbApi.suggestion(aid!),
    enabled: !!aid,
    staleTime: 60_000,
    refetchInterval: (q) => (q.state.data?.pending ? 10_000 : false),
  });
}

/**
 * Из чего оплатим (правило Р38): сначала бонус, потом баланс; не хватает — сколько.
 */
export function fundsLine(funds: Funds | undefined, total: number) {
  if (!funds) return { text: '', short: 0 };
  const bal = Number(funds.available_rub ?? funds.balance_rub);
  const bonus = Number(funds.bonus_left_rub);
  const f = (n: number) =>
    new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
      n,
    ) + ' ₽';
  if (total > bal)
    return { text: '', short: Math.round((total - bal) * 100) / 100, balance: bal, bonus };
  if (bonus >= total && bonus > 0)
    return {
      text: `Оплатим из приветственного бонуса: ${f(bonus)} → останется ≈ ${f(bonus - total)}`,
      short: 0,
    };
  if (bonus > 0)
    return {
      text: `Из бонуса ${f(bonus)}, остальное с баланса · после списания на балансе ≈ ${f(bal - total)}`,
      short: 0,
    };
  return {
    text: `Бонус израсходован · баланс ${f(bal)} → после списания ≈ ${f(bal - total)}`,
    short: 0,
  };
}
